import api, { deduplicatedApi } from "./index";

// Register property API call
export const registerProperty = (formData) =>
  api.post("property-management/register_property/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
// Live profiles already at this address (Add Property / Add Appraisal warning).
// params: { address, suburb, exclude_id? }
export const checkDuplicateProperty = (params) =>
  api.get("property-management/properties/check-duplicate/", { params });
// "Add to this profile": the Add form's data merged into the live profile
// already at the address, instead of a second profile (CRM-2026-00081).
export const mergeIntoProperty = (propertyId, formData) =>
  api.post(`property-management/properties/${propertyId}/merge-in/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
// GET property API call
// url: optional string for pagination, params: optional object for query params (e.g., search)
// Using deduplicatedApi to prevent duplicate simultaneous requests
export const getProperties = (params = {}) => {
  // if (url) {
  //   // If a full URL is provided (pagination), ignore params
  //   return deduplicatedApi.get(url);
  // }
  return deduplicatedApi.get("property-management/properties/", { params });
};

export const getProperty = (id) =>
  deduplicatedApi.get(`property-management/properties/${id}/`);

// realestate.com.au campaign performance stats for a live REA listing
// (Property Price Statement views etc.) - backend caches REA's report for 12h.
export const getReaListingPerformance = (reaListingId) =>
  deduplicatedApi.get(`property-crawler/rea-performance/${reaListingId}/`);

// The agent switcher above the Off / On Market Pipeline boards: every agent in
// the office with the number of appraisals they are holding on THIS board, so
// an admin can look at one agent at a time the way the Database already lets
// them (Michael Piccoli, Monday 13091474717).
//
// Returns { agents: [] } for anyone below the admin tier rather than a 403, so
// the switcher simply does not render for an agent.
export const getPipelineAgents = (appraisalPipeline) =>
  api.get("property-management/properties/pipeline-agents/", {
    params: appraisalPipeline ? { appraisal_pipeline: appraisalPipeline } : undefined,
  });

// The super admin's agent switcher on Collings Properties: every sales agent
// (plus anyone holding listings) with how many listings they hold. Empty for
// anyone below super admin (Christian Gravias, Monday 13202768457).
export const getListingAgents = (params = {}) =>
  api.get("property-management/properties/listing-agents/", { params });

// The signed-in user's team: the agents and assistants on their desk. Drives
// the My Listings switcher and Agent Performance for a team lead (Christian
// Gravias, Monday 13225574145). Empty list = no team.
export const getMyTeam = () =>
  api.get("role-management/my-team/");

// Appraisal submissions — the review queue of Block of Units vendor intake
// forms. Approving creates the CRM Contact + Property; rejecting discards.
export const getAppraisalSubmissions = (params = {}) =>
  api.get("property-management/appraisal-submissions/", { params });

export const approveAppraisalSubmission = (id) =>
  api.post(`property-management/appraisal-submissions/${id}/approve/`);

export const rejectAppraisalSubmission = (id, reason = "") =>
  api.post(`property-management/appraisal-submissions/${id}/reject/`, { reason });

// DELETE property API call
export const deleteProperty = (id) =>
  api.delete(`property-management/properties/${id}/`);
// UPDATE property API call (full update with files)
export const updateProperty = (id, formData) =>
  api.put(`property-management/properties/${id}/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// PATCH property API call (partial update - for simple field updates like visibility)
export const patchProperty = (id, data) => {
  // Convert plain object to FormData for the backend
  const formData = new FormData();
  // Keys that must be sent even when null/empty so backend can clear them
  const clearableKeys = ['weekly_rent', 'co_agent', 'tertiary_agent'];
  Object.keys(data).forEach(key => {
    const value = data[key];
    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false');
    } else if (value !== null && value !== undefined) {
      formData.append(key, value);
    } else if (clearableKeys.includes(key)) {
      formData.append(key, '');
    }
  });
  return api.put(`property-management/properties/${id}/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Pipeline actions — "Listed" promotes an appraisal to a draft listing (lands
// in My Listings, not on any portal); "Lost" marks it lost (withdraws the card
// + marks the linked seller lead lost, same reason list as the holding area).
export const markPropertyListed = (id) =>
  api.post(`property-management/properties/${id}/mark-listed/`);

// Ask AI-SEO to draft an article about this listing (agent-initiated). Server
// verifies the caller is the listing/co/tertiary agent (or admin) and queues a
// DRAFT into the AI-SEO review queue — never auto-publishes.
export const draftArticle = (id) =>
  api.post(`property-management/properties/${id}/draft-article/`);

export const markPropertyLost = (id, reason, note = "") =>
  api.post(`property-management/properties/${id}/mark-lost/`, { reason, note });

// "Move to Appraisal" on a Prospects LOST card: a lost appraisal or lost sales
// listing back on the Appraisal column, its seller lead no longer Lost.
export const moveLostPropertyToAppraisal = (id) =>
  api.post(`property-management/properties/${id}/move-to-appraisal/`);

// A Prospects property card to any column but Sold / Lost (those have their own
// forms): "new" | "connected" | "long_term" back to its seller lead, or
// "appraisal" | "listed" | "for_sale" (CRM-2026-00115; Christian Gravias, 21 Oct
// 2026: "any card can be moved anywhere"). A Sold card is un-sold on the way.
export const movePropertyBack = (id, to) =>
  api.post(`property-management/properties/${id}/move-back/`, { to });

// Reorder images for a standard CRM property (lightweight PATCH)
export const reorderPropertyImages = (id, images) =>
  api.patch(
    `property-management/properties/${id}/reorder-images/`,
    { images },
    { headers: { 'Content-Type': 'application/json' } }
  );

// The call-back series booked on an appraisal. The Add / Edit Appraisal forms
// book theirs through patchProperty (`appraisal_reminders`); these read the
// series back, book one on its own and drop one entry (Simon Abbott, Monday
// 12985995388 and 13036305197).
export const getAppraisalReminders = (propertyId, params = {}) =>
  api.get(`property-management/properties/${propertyId}/appraisal-reminders/`, { params });

// Book a series without touching the rest of the property - what the Notes tab
// needs, where the agent is writing a note and not editing the listing.
// `reminders` is [{ date: ISO string, reason }], `note` the note they typed.
export const createAppraisalReminders = (propertyId, reminders, note = "") =>
  api.post(
    `property-management/properties/${propertyId}/appraisal-reminders/`,
    { reminders, note },
    { headers: { 'Content-Type': 'application/json' } }
  );

export const deleteAppraisalReminder = (propertyId, kind, reminderId) =>
  api.delete(
    `property-management/properties/${propertyId}/appraisal-reminders/${kind}/${reminderId}/`
  );

// Get documents for a property (CRM or REA/Collings id)
export const getPropertyDocuments = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/documents/`);

// Upload documents (single request) for a property
export const uploadPropertyDocuments = (propertyId, statementFiles = [], contractFiles = [], legalDocsFiles = [], documentNames = {}) => {
  const formData = new FormData();

  // Helper function to rename file
  const renameFile = (file, newName, type, index) => {
    const key = `${type}-${index}-${file.name}`;
    const customName = documentNames[key];
    if (customName && customName.trim()) {
      const extension = file.name.split('.').pop();
      const renamedFile = new File([file], `${customName.trim()}.${extension}`, { type: file.type });
      return renamedFile;
    }
    return file;
  };

  statementFiles.forEach((file, index) => {
    const renamedFile = renameFile(file, null, 'statement', index);
    formData.append("statement_of_info_docs", renamedFile);
  });

  contractFiles.forEach((file, index) => {
    const renamedFile = renameFile(file, null, 'contract', index);
    formData.append("contract_docs", renamedFile);
  });

  legalDocsFiles.forEach((file, index) => {
    const renamedFile = renameFile(file, null, 'legal', index);
    formData.append("legal_docs", renamedFile);
  });

  return api.post(
    `property-management/properties/${propertyId}/documents/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

// Upload a mixed batch of documents where each item carries its own category.
// items: [{ file: File, category: string, name?: string }]
// `category` must be one of the backend PropertyDocument category values, e.g.
// "statement_of_info_docs", "contract_docs", "lease_docs",
// "property_report_docs", "legal_docs", "other_docs".
export const uploadPropertyDocumentsByCategory = (propertyId, items = []) => {
  const formData = new FormData();
  items.forEach(({ file, category, name }) => {
    if (!file || !category) return;
    let outFile = file;
    if (name && name.trim()) {
      const extension = file.name.split('.').pop();
      outFile = new File([file], `${name.trim()}.${extension}`, { type: file.type });
    }
    formData.append(category, outFile);
  });
  return api.post(
    `property-management/properties/${propertyId}/documents/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

// REA Publishing APIs
export const publishPropertyToREA = (propertyId) =>
  api.post(`property-management/properties/${propertyId}/publish-rea/`);

export const unpublishPropertyFromREA = (propertyId) =>
  api.post(`property-management/properties/${propertyId}/unpublish-rea/`);

export const previewREAXML = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/preview-reaxml/`);

export const testREAConnection = () =>
  api.get("property-management/test-rea-connection/");

// confirmReaSold: the agent reviewed the sold details and accepted they are
// final on realestate.com.au (required for the first REA push of a sale).
export const publishSoldToPortals = (propertyId, channels, { confirmReaSold = false } = {}) =>
  api.post(`property-management/properties/${propertyId}/publish-sold/`, {
    channels,
    confirm_rea_sold: confirmReaSold,
  });

// Withdraw a listing with a reason - see PropertyWithdrawView. Reason choices
// depend on the property's current status (Appraisal vs. On/Off Market
// Listing), re-validated server-side against reason_choices_for_status().
export const withdrawProperty = (propertyId, reason, reasonNote = "") =>
  api.post(`property-management/properties/${propertyId}/withdraw/`, {
    reason,
    reason_note: reasonNote,
  });

// Capture a rental's lease outcome in the dedicated structured fields
// (is_leased / achieved_rent / lease_commencement_date + a property_leased
// event). Complements the tenancy PATCH, which only overloads the sale columns
// for display. The endpoint also accepts an optional tenant_contact_id to link
// the tenant, but the current tenancy modal doesn't collect one, so this path
// doesn't send it.
export const markPropertyLeased = (propertyId, data) =>
  api.post(`property-management/properties/${propertyId}/mark-leased/`, data);

// Upload Statement of Info documents (multiple files)
export const uploadStatementOfInfo = (propertyId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("statement_of_info_docs", file));
  return api.post(
    `property-management/properties/${propertyId}/documents/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

// Upload Contract documents (multiple files)
export const uploadContractFiles = (propertyId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("contract_docs", file));
  return api.post(
    `property-management/properties/${propertyId}/documents/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

// Upload Legal documents (multiple files)
export const uploadLegalDocuments = (propertyId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("legal_docs", file));
  return api.post(
    `property-management/properties/${propertyId}/documents/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

// Delete a single property document
export const deletePropertyDocument = (documentId) =>
  api.delete(`property-management/property-documents/${documentId}/`);

// Mark a PPS/Statement doc as the main one that feeds REA & Domain
export const setPrimaryPropertyDocument = (documentId) =>
  api.post(`property-management/property-documents/${documentId}/set-primary/`);

// Show or hide a (non-PPS) document on the buyer portal & public website
export const setPropertyDocumentOnline = (documentId, showOnline) =>
  api.post(`property-management/property-documents/${documentId}/set-online/`, {
    show_online: showOnline,
  });

// EMAIL property documents
export const emailPropertyDocuments = (propertyId, payload) =>
  api.post(
    `property-management/properties/${propertyId}/documents/send-email/`,
    payload
  );

// AGENT MANAGEMENT
// Build FormData when an actual File is included so multipart/form-data is used
// for profile picture uploads. Falls back to JSON when no file is present so
// existing callers continue to work unchanged.
const containsFile = (data) =>
  data && Object.values(data).some((v) => typeof File !== "undefined" && v instanceof File);

const buildAgentPayload = (agentData) => {
  if (!containsFile(agentData)) {
    return { data: agentData, headers: undefined };
  }
  const fd = new FormData();
  Object.entries(agentData || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    // Skip empty strings for the optional file so backend doesn't try to clear it
    if (key === "profile_pic" && !(value instanceof File)) return;
    fd.append(key, value);
  });
  return {
    data: fd,
    headers: { "Content-Type": "multipart/form-data" },
  };
};

export const addAgent = (agentData) => {
  const { data, headers } = buildAgentPayload(agentData);
  return api.post("role-management/agents/", data, headers ? { headers } : undefined);
};

export const updateAgent = (id, agentData) => {
  const { data, headers } = buildAgentPayload(agentData);
  return api.put(`role-management/agents/${id}/`, data, headers ? { headers } : undefined);
};

// Update the logged-in user's own profile (self-service, any role). Used for
// fields an agent can set for themselves, e.g. their SMS sender number.
export const updateMyProfile = (data) =>
  api.patch("role-management/profile/", data);

export const getAgents = (params = {}) =>
  api.get("role-management/agents/", { params });

export const getAgent = (id) =>
  api.get(`role-management/agents/${id}/`);

export const deleteAgent = (id) => api.delete(`role-management/agents/${id}/`);

export const archiveAgent = (id) => api.delete(`role-management/agents/${id}/`);

export const restoreAgent = (id) => api.patch(`role-management/agents/${id}/`);

export const resendAgentSetupEmail = (id) =>
  api.post(`role-management/agents/${id}/resend-setup-email/`);

export const getArchivedAgents = (params = {}) =>
  api.get("role-management/agents/", { params: { ...params, archived: 'true' } });

// AI Generation
export const generateAIDescription = (propertyData) =>
  api.post("property-management/properties/generate-description/", propertyData);

// Property Interactions (Enquiries, Bids, Offers)
export const getPropertyEnquiries = (id) =>
  api.get(`property-management/properties/${id}/enquiries/`);

export const updateEnquiryStatus = (id, data) =>
  api.patch(`property-management/enquiries/${id}/`, data);

export const getPropertyBidderRegistrations = (id) =>
  api.get(`property-management/properties/${id}/bidder-registrations/`);

export const updateBidderRegistration = (id, data) =>
  api.patch(`property-management/bidder-registrations/${id}/`, data);

export const getPropertyOffers = (id) =>
  api.get(`property-management/properties/${id}/offers/`);

export const updateOffer = (id, data) =>
  api.patch(`property-management/offers/${id}/`, data);

export const getPropertyInteractionsSummary = (id) =>
  api.get(`property-management/properties/${id}/interactions-summary/`);

// Property Notes
export const getPropertyNotes = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/notes/`);

export const createPropertyNote = (propertyId, noteData) =>
  api.post(`property-management/properties/${propertyId}/notes/`, noteData);

export const updatePropertyNote = (noteId, noteData) =>
  api.patch(`property-management/notes/${noteId}/`, noteData);

export const deletePropertyNote = (noteId) =>
  api.delete(`property-management/notes/${noteId}/`);

// PERSONAL ASSISTANT (PA) MANAGEMENT — Admin only
/**
 * Personal Assistants (Settings > My Assistant).
 *
 * Open to every CRM user, not just admins, and a user may have SEVERAL PAs
 * (31 Aug 2026 - Christian: "My assistant is available for agents, only for
 * admins it seems ... We need agents to be able to use it", and add multiple).
 * The endpoint therefore returns `{ assistants: [...], max: N }` and identifies
 * an assistant by id on the write calls.
 */
export const getMyAssistants = () =>
  api.get("role-management/my-assistant/");

export const inviteAssistant = (data) =>
  api.post("role-management/my-assistant/", data);

export const removeAssistant = (id) =>
  api.delete("role-management/my-assistant/", { data: { id } });

export const updateAssistantAccess = (id, pa_access_level) =>
  api.patch("role-management/my-assistant/", { id, pa_access_level });

/**
 * Colleagues who could be asked to assist: the team dropdown.
 *
 * Picking a name is how an existing agent becomes an assistant now. Typing their
 * email used to overwrite their account and switch their own login off, which is
 * how two agents lost theirs (Monday CRM-2026-00028).
 */
export const getAssistantCandidates = () =>
  api.get("role-management/my-assistant/candidates/");

/** Ask a colleague, by their CRM user id, rather than by typing an address. */
export const inviteAssistantById = (user_id, pa_access_level) =>
  api.post("role-management/my-assistant/", { user_id, pa_access_level });

// The invitee's side, opened from the emailed link. Public: the token is the
// proof, because they may not be logged in where they read their mail.
export const getAssistantInvite = (token) =>
  api.get("role-management/assistant-invite/", { params: { token } });

export const answerAssistantInvite = (token, action) =>
  api.post("role-management/assistant-invite/", { token, action });
